import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { InputType } from '../../types/input.ts';
import FormInput from '../inputs/FormInput.tsx';
import FormTextArea from '../inputs/FormTextArea.tsx';
import Button from '../buttons/Button.tsx';
import { ButtonSize, ButtonType, ButtonVariant } from '../../types/button.ts';
import ButtonLink from '../buttons/ButtonLink.tsx';
import ValidatorsHelper from '../../helpers/validatorsHelper.ts';
import apiNet from '../../newApi/apiNet.ts';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNotification } from '../../providers/NotificationProvider.tsx';
import { NotificationStatus } from '../notification/Notification.tsx';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import CircleLoader from '../loaders/CircleLoader.tsx';
import { CONTACT_TOPICS } from '../../consts/contact/contactTopics.ts';

const messageMinLength = 100;
const titleMinLength = 10;

const ContactForm = () => {
    const _recaptchaService = useGoogleReCaptcha();
    const _notificationService = useNotification();

    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedTopic, setSelectedTopic] = useState('');
    const [selectedTopicIsValid, setSelectedTopicIsValid] = useState(false);
    const [email, setEmail] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(false);

    const [title, setTitle] = useState('');
    const [titleIsValid, setTitleIsValid] = useState(false);
    const [message, setMessage] = useState('');
    const [messageIsValid, setMessageIsValid] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const setInitialValue = () => {
        setTitle('');
        setTitleIsValid(false);
        setMessage('');
        setMessageIsValid(false);
        setShowErrors(false);
        setMessage('');
        setMessageIsValid(false);
        setSelectedTopic('');
        setSelectedTopicIsValid(false);
    };

    const handleSelectedTopic = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedTopic(event.currentTarget.value);
        setSelectedTopicIsValid(event.currentTarget.value !== '');
    };
    const handleEmailChange = (event: FormEvent<HTMLInputElement>) => {
        setEmail(event.currentTarget.value);
        setEmailIsValid(ValidatorsHelper.isEmailValid(event.currentTarget.value));
    };
    const handleTitleChange = (event: FormEvent<HTMLInputElement>) => {
        setTitle(event.currentTarget.value);
        setTitleIsValid(event.currentTarget.value.length > titleMinLength);
    };
    const handleMessageChange = (event: FormEvent<HTMLTextAreaElement>) => {
        setMessage(event.currentTarget.value);
        setMessageIsValid(event.currentTarget.value.length > messageMinLength);
    };

    const handleSubmitEvent = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (selectedTopicIsValid && emailIsValid && titleIsValid && messageIsValid) {
            if (_recaptchaService.executeRecaptcha) {
                try {
                    setIsLoading(true);
                    const reCaptchaToken = await _recaptchaService.executeRecaptcha();
                    await apiNet.contactFormSendMessage({
                        email,
                        message,
                        topic: selectedTopic,
                        reCaptchaToken,
                        title,
                    });
                    _notificationService.showNotification('Sukces', 'Wiadomość została wysłana pomyślnie.', NotificationStatus.success);
                    setInitialValue();
                    setIsSuccess(true);
                    setIsLoading(false);
                } catch (e) {
                    setIsLoading(false);
                    _notificationService.showNotification('Niepowodzenie', 'Wystapił błąd po stronie serwera. Proszę spróbować ponownie za kilka minut.', NotificationStatus.failure);
                }
            }
        } else {
            setShowErrors(true);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            setInitialValue();
            setIsSuccess(false);
        };
    }, []);

    return (
        <>
            {!isSuccess &&
                <form action="/contact-form" className={'w-full space-y-3 text-left'}
                      onSubmit={(e) => handleSubmitEvent(e)}
                >
                    <label className={'flex flex-col'} htmlFor="options">Wybierz temat:
                        <select
                            id="options"
                            value={selectedTopic}
                            onChange={handleSelectedTopic}
                            className={`appearance-none bg-neutral-700 rounded-full text-white border border-neutral-700 px-3 py-2 mt-2 ring-2 ring-offset-2 ring-transparent focus:ring-[#008DD2] ring-offset-neutral-800 outline-hidden transition-all`}
                        >
                            <option value="">-- Wybierz temat --</option>
                            {CONTACT_TOPICS.map(value =>
                                <option value={value.id}>{value.label}</option>,
                            )}
                        </select>
                    </label>
                    {showErrors && !selectedTopicIsValid &&
                        <p className="text-red-500">Wybierz temat rozmowy</p>
                    }
                    <FormInput
                        label="Email"
                        name="email"
                        value={email}
                        type={InputType.TEXT}
                        onInputChange={handleEmailChange}
                        className="ring-offset-neutral-900 py-2 bg-neutral-700"
                    />
                    {showErrors && !emailIsValid &&
                        <p className="text-red-500">Nieprawidłowy adres email</p>
                    }
                    <FormInput
                        name="title"
                        label="Tytuł"
                        value={title}
                        type={InputType.TEXT}
                        onInputChange={handleTitleChange}
                        className="ring-offset-neutral-900 py-2 bg-neutral-700"
                    />
                    {showErrors && !titleIsValid &&
                        <p className="text-red-500">Tytuł musi zawierać conajmniej {titleMinLength} znaków</p>
                    }
                    <FormTextArea
                        name="message"
                        label="Wiadomość"
                        value={message}
                        onInputChange={handleMessageChange}
                        className="ring-offset-neutral-900 py-2 bg-neutral-700"
                    />
                    {showErrors && !messageIsValid &&
                        <p className="text-red-500">Wiadomość musi zawierać conajmniej {messageMinLength} znaków</p>
                    }
                    <p className={'text-xs text-neutral-400 py-3'}>Ta strona korzysta z usługi reCAPTCHA. Usługę
                        reguluje <ButtonLink to={'https://policies.google.com/terms'}
                                             variant={ButtonVariant.SECONDARY}
                                             targetType={'_blank'}
                                             className={'text-xs pl-0 pr-0 pt-0 pb-0'}>regulamin
                            usługi</ButtonLink> Google.</p>
                    <Button
                        type={ButtonType.SUBMIT}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.BASE}
                        disabled={isLoading}
                        className={'pl-10 pr-10 py-1.5 ring-offset-neutral-900 flex items-center space-x-2 w-full justify-center'}
                    >{isLoading && <CircleLoader className="w-5 mr-2" />}Wyślij</Button>
                </form>
            }
            {isSuccess &&
                <section className={'text-center'}>
                    <CheckCircleIcon className={'w-16 h-16 text-green-400 mx-auto mb-4 rounded-full'} />
                    <h1 className={'text-3xl mb-4'}>Wiadomość wysłana</h1>
                    <p className={'text-neutral-400 mb-5'}>
                        Twoja wiadomość została pomyślnie wysłana.
                    </p>
                </section>
            }
        </>
    );
};

export default ContactForm;